import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Shield } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"
import { api } from "@/lib/api"
import { useAuthStore } from "@/stores/authStore"

import { Button } from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

const loginSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
    password: z.string().min(1, "Password is required"),
})

type LoginFormValues = z.infer<typeof loginSchema>

export default function AdminLoginPage() {
    const navigate = useNavigate()
    const { setAuth } = useAuthStore()
    const [loading, setLoading] = useState(false)

    const form = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: "",
            password: "",
        },
    })

    async function onSubmit(values: LoginFormValues) {
        try {
            setLoading(true)
            const { data } = await api.post("/api/auth/login", values)

            if (data.data.user.role !== "superadmin") {
                toast.error("Unauthorized access. Admin privileges required.")
                return
            }

            setAuth(data.data.user, data.data.token)
            toast.success("Welcome back, Admin")
            navigate("/admin")
        } catch (err: any) {
            toast.error(err.response?.data?.error || "Invalid superadmin credentials")
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
            <div className="w-full max-w-sm space-y-8">
                <div className="flex flex-col items-center space-y-2">
                    <div className="h-12 w-12 bg-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-600/20">
                        <Shield className="h-6 w-6 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight mt-4">RentBase Admin</h1>
                    <p className="text-sm text-muted-foreground">
                        Sign in with superadmin credentials
                    </p>
                </div>

                <div className="bg-card border border-border/50 rounded-2xl p-6 shadow-xl">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Admin Email</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="email"
                                                placeholder="admin@rentbase.io"
                                                {...field}
                                                className="bg-background"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <div className="flex items-center justify-between">
                                            <FormLabel>Password</FormLabel>
                                        </div>
                                        <FormControl>
                                            <Input
                                                type="password"
                                                placeholder="••••••••"
                                                {...field}
                                                className="bg-background"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button
                                type="submit"
                                className="w-full bg-red-600 hover:bg-red-700 text-white shadow-md shadow-red-600/20 mt-2"
                                disabled={loading}
                            >
                                {loading ? "Authenticating..." : "Access Dashboard"}
                            </Button>
                        </form>
                    </Form>
                </div>
            </div>
        </div>
    )
}
